/**
 * ZOY-372: a failed save never unmounts the editor. The message states what
 * actually happened — a server refusal (4xx, with the server's own reason
 * when it gives one) is never described as a connection problem. Shared by
 * the clinic-setup editors (ZOY-637 brought rooms and practitioner details
 * onto it).
 */

import { useIntl } from 'react-intl';
import { Notice } from '@/ui/kit';
import { ApiError, NetworkError } from '@/core/api/errors';

export type SaveError =
  { kind: 'network' } | { kind: 'refused'; serverMessage?: string } | { kind: 'other' };

export function saveErrorOf(err: unknown): SaveError {
  if (err instanceof NetworkError) return { kind: 'network' };
  if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
    return { kind: 'refused', serverMessage: err.serverMessage };
  }
  return { kind: 'other' };
}

export function SaveErrorNotice({ error }: { error: SaveError }) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  if (error.kind === 'network') {
    return (
      <Notice kind="err" role="alert" title={t('signin.netTitle')}>
        {t('signin.netBody')}
      </Notice>
    );
  }
  if (error.kind === 'refused') {
    return (
      <Notice kind="err" role="alert">
        {t('profile.saveRefused')}
        {error.serverMessage && (
          <span style={{ display: 'block', marginBlockStart: 4, fontSize: 12 }} dir="ltr">
            {error.serverMessage}
          </span>
        )}
      </Notice>
    );
  }
  return (
    <Notice kind="err" role="alert">
      {t('profile.saveFailed')}
    </Notice>
  );
}
